"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { ScreeningCard } from "@/components/tools/research-discovery/screening-card";
import { ScreeningLoader } from "@/components/tools/research-discovery/screening-loader";
import { ScreeningSummaryPanel } from "@/components/tools/research-discovery/screening-summary";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { RefRecord } from "@/lib/tool-kit/research-discovery/records";
import {
  afterDecision,
  clampPosition,
  type Decision,
  FILTERS,
  type Filter,
  findDuplicates,
  loadRecords,
  parseTerms,
  summarize,
  type Verdict,
  type Verdicts,
  visibleIndices,
} from "@/lib/tool-kit/research-discovery/screening";

/** English strings. A translated content file overrides these through `ui`. */
export const ABSTRACT_SCREENING_TOOL_STRINGS = {
  inputLabel: "Your records — RIS, CSV, PubMed .nbib or EndNote XML",
  inputPlaceholder:
    "TY  - JOUR\nTI  - Title of a record\nAB  - Its abstract…\nER  - ",
  inputHint:
    "Paste the text or upload a file. A CSV needs a Title column and may have Abstract, Authors, Year, Journal, DOI and Keywords columns; a CSV saved by this tool also brings back your decisions. Records are read in your browser — nothing is uploaded.",
  upload: "Upload a file",
  uploadLabel: "Choose a file of records to screen",
  trySample: "Try a sample",
  load: "Start screening",
  sample:
    "TY  - JOUR\nAU  - Example, Alex\nAU  - Sample, Sam\nTI  - Mindfulness training for exam anxiety in undergraduate students: a randomised controlled trial\nT2  - Journal of Sample Studies\nPY  - 2021\nAB  - We randomly assigned 120 undergraduates to an eight-week mindfulness course or a waitlist control. Exam anxiety, measured with a validated scale, was lower in the mindfulness group at the end of the course.\nKW  - mindfulness\nKW  - exam anxiety\nER  - \n\nTY  - JOUR\nAU  - Example, Alex\nTI  - Mindfulness and stress responses in laboratory rats\nT2  - Journal of Sample Studies\nPY  - 2019\nAB  - Rats were exposed to a mindfulness-like handling protocol for four weeks. Corticosterone levels fell in treated animals compared with controls.\nER  - \n\nTY  - JOUR\nAU  - Placeholder, Pat\nTI  - Teachers' views on supporting anxious students: a qualitative interview study\nT2  - Sample Education Review\nPY  - 2020\nAB  - Twenty secondary-school teachers described how they help students with exam anxiety. Themes included reassurance, routine and referral.\nER  - \n\nTY  - JOUR\nAU  - Sample, Sam\nTI  - Interventions for test anxiety: a systematic review\nT2  - Sample Education Review\nPY  - 2018\nAB  - This systematic review of randomised trials found that cognitive-behavioural and relaxation approaches reduced test anxiety in students, with low certainty of evidence.\nER  - \n\nTY  - JOUR\nAU  - Example, Alex\nAU  - Sample, Sam\nTI  - Mindfulness training for exam anxiety in undergraduate students: a randomised controlled trial\nT2  - Journal of Sample Studies\nPY  - 2021\nAB  - We randomly assigned 120 undergraduates to an eight-week mindfulness course or a waitlist control.\nER  - \n",
  problems: {
    empty: "Paste or upload some records first.",
    unrecognised:
      "These records are not in a format the tool reads. Use RIS, CSV, a PubMed .nbib file or an EndNote XML export.",
    noTitleColumn:
      "The CSV needs a column headed Title (or Article Title) in its first row.",
    noRecords:
      "No records with a title or an abstract were found in this file.",
  },
  loaded:
    "one:{count} record loaded from {format}.|other:{count} records loaded from {format}.",
  formats: {
    ris: "RIS",
    csv: "CSV",
    nbib: "PubMed .nbib",
    endnote: "EndNote XML",
  },
  resumed:
    "one:{count} earlier decision was restored.|other:{count} earlier decisions were restored.",
  newFile: "Load different records",
  undoLast: "Undo last decision",
  undone: "Decision on record {record} undone.",
  filterLabel: "Show",
  filters: {
    undecided: "Unscreened",
    all: "All",
    include: "Included",
    maybe: "Maybe",
    exclude: "Excluded",
  },
  filterEmpty: "No records in this view.",
  allDone: "Every record has a decision. Download your file below.",
  termsHeading: "Highlight words",
  includeTermsLabel: "Words that suggest a record fits (comma-separated)",
  excludeTermsLabel: "Words that suggest it does not",
  includeTermsPlaceholder: "randomised, trial, students",
  excludeTermsPlaceholder: "rats, mice, in vitro",
  termsHint:
    "Matching ignores case and works inside words, so “random” marks “randomised”. If a word is in both lists it is marked as an exclude word.",
  cardLabel: "Record to screen",
  position: "Record {position} of {total} in this view",
  previous: "Previous",
  next: "Next",
  untitled: "Untitled record",
  noAbstract: "This record has no abstract.",
  keywords: "Keywords:",
  etAl: "et al.",
  duplicate:
    "Possible duplicate of record {record} — the same DOI, or the same title and year.",
  decisions: { include: "Include", maybe: "Maybe", exclude: "Exclude" },
  undo: "Clear decision",
  reasonLabel: "Reason for excluding",
  reasonNone: "No reason given",
  reasonHint: "Press 1–7 to exclude with that reason.",
  reasons: [
    "Wrong population",
    "Wrong intervention or exposure",
    "Wrong outcome",
    "Wrong study design",
    "Wrong publication type",
    "Wrong language or date",
    "Duplicate",
  ],
  noteLabel: "Note",
  notePlaceholder: "Optional",
  shortcutsHeading: "Keyboard",
  shortcuts:
    "I include · M maybe · E exclude · 1–7 exclude with a reason · U clear · Z undo last · ← → move. Shortcuts work when no text box has the cursor.",
  announce: "{decision}. {screened} of {total} screened.",
  summaryHeading: "Screening summary",
  statTotal: "Records",
  statScreened: "Screened",
  statUndecided: "Not yet screened",
  reasonsHeading: "Exclusion reasons",
  colReason: "Reason",
  colCount: "Records",
  prismaHeading: "Numbers for a PRISMA flow diagram",
  prismaLine:
    "Records screened: {screened}. Records excluded: {excluded}. Reports sought for retrieval: {sought} ({included} included, {maybe} maybe).",
  prismaNote:
    "Duplicates removed before screening are counted separately in the flow diagram.",
  flowLink: "PRISMA Flow Diagram Generator",
  fileName: "screening",
  downloadCsv: "Download decisions (.csv)",
  downloadIncluded: "Download included as RIS ({count})",
  downloadSought: "Download included and maybe as RIS ({count})",
  copyCounts: "Copy the numbers",
  copied: "Copied!",
  saveNote:
    "Nothing is saved on this page. Download the decisions CSV to keep your work, and load it again later to carry on where you stopped.",
} as const;

export type AbstractScreeningToolStrings =
  typeof ABSTRACT_SCREENING_TOOL_STRINGS;

const KEY_DECISION: Record<string, Decision> = {
  i: "include",
  m: "maybe",
  e: "exclude",
};
const EDITABLE = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** A decision that can be taken back: the record and what it had before. */
interface HistoryEntry {
  index: number;
  previous: Verdict | null;
}

const HISTORY_LIMIT = 200;

export default function AbstractScreeningToolWidget() {
  const strings = useToolUi(ABSTRACT_SCREENING_TOOL_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [problem, setProblem] = useState<string | null>(null);
  const [records, setRecords] = useState<RefRecord[]>([]);
  const [format, setFormat] = useState<string>("");
  const [restored, setRestored] = useState(0);
  const [verdicts, setVerdicts] = useState<Verdicts>({});
  const [filter, setFilter] = useState<Filter>("undecided");
  const [cursor, setCursor] = useState(0);
  const [terms, setTerms] = useState({ include: "", exclude: "" });
  const [announcement, setAnnouncement] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const refocus = useRef<number | null>(null);
  const keyHandler = useRef<(event: KeyboardEvent) => void>(() => undefined);

  const duplicates = useMemo(() => findDuplicates(records), [records]);
  const summary = useMemo(
    () => summarize(records.length, verdicts),
    [records.length, verdicts]
  );
  const visible = useMemo(
    () => visibleIndices(records.length, verdicts, filter),
    [records.length, verdicts, filter]
  );
  const position = clampPosition(cursor, visible.length);
  const current = visible[position];

  function load(source: string) {
    const result = loadRecords(source);
    if (result.problem) {
      setProblem(result.problem);
      return;
    }
    setProblem(null);
    setRecords(result.records);
    setVerdicts(result.verdicts);
    setRestored(Object.keys(result.verdicts).length);
    setFormat(result.format ?? "");
    setFilter("undecided");
    setCursor(0);
    setAnnouncement("");
  }

  function setVerdict(index: number, next: Verdict | null) {
    setVerdicts((currentVerdicts) => {
      const copy = { ...currentVerdicts };
      if (next) {
        copy[index] = next;
      } else {
        delete copy[index];
      }
      return copy;
    });
  }

  /** Change a verdict and remember how to take the change back. */
  function change(index: number, next: Verdict | null) {
    setHistory((entries) =>
      [...entries, { index, previous: verdicts[index] ?? null }].slice(
        -HISTORY_LIMIT
      )
    );
    setVerdict(index, next);
  }

  function undoLast() {
    const last = history.at(-1);
    if (!last) {
      return;
    }
    setHistory((entries) => entries.slice(0, -1));
    setVerdict(last.index, last.previous);
    refocus.current = last.index;
    setAnnouncement(t(strings.undone, { record: fmt(last.index + 1) }));
  }

  function decide(decision: Decision, reason?: string) {
    if (current === undefined) {
      return;
    }
    const before = verdicts[current];
    change(current, {
      decision,
      reason: decision === "exclude" ? (reason ?? before?.reason ?? "") : "",
      note: before?.note ?? "",
    });
    const screened = summary.screened + (before ? 0 : 1);
    setAnnouncement(
      t(strings.announce, {
        decision: strings.decisions[decision],
        screened: fmt(screened),
        total: fmt(records.length),
      })
    );
    setCursor(afterDecision(filter, position));
  }

  function move(step: -1 | 1) {
    setCursor(clampPosition(position + step, visible.length));
  }

  keyHandler.current = (event) => {
    const target = event.target as HTMLElement | null;
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      (target && (EDITABLE.has(target.tagName) || target.isContentEditable))
    ) {
      return;
    }
    const key = event.key.toLowerCase();
    const reasonIndex = Number(key) - 1;
    if (KEY_DECISION[key]) {
      decide(KEY_DECISION[key]);
    } else if (reasonIndex >= 0 && reasonIndex < strings.reasons.length) {
      decide("exclude", strings.reasons[reasonIndex]);
    } else if (key === "u" && current !== undefined) {
      change(current, null);
    } else if (key === "z") {
      undoLast();
    } else if (key === "arrowleft") {
      move(-1);
    } else if (key === "arrowright") {
      move(1);
    } else {
      return;
    }
    event.preventDefault();
  };

  const active = records.length > 0;
  useEffect(() => {
    if (!active) {
      return;
    }
    const listener = (event: KeyboardEvent) => keyHandler.current(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [active]);

  if (!active) {
    return (
      <ScreeningLoader onLoad={load} problem={problem} strings={strings} />
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-foreground text-sm">
          {tPlural(strings.loaded, locale, records.length, {
            count: fmt(records.length),
            format:
              (strings.formats as Record<string, string>)[format] ?? format,
          })}
          {restored > 0
            ? ` ${tPlural(strings.resumed, locale, restored, { count: fmt(restored) })}`
            : ""}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            aria-keyshortcuts="Z"
            className={BUTTON_SECONDARY}
            disabled={history.length === 0}
            onClick={undoLast}
            type="button"
          >
            {strings.undoLast} (Z)
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setRecords([]);
              setVerdicts({});
              setHistory([]);
            }}
            type="button"
          >
            {strings.newFile}
          </button>
        </div>
      </div>
      <PillGroup
        label={strings.filterLabel}
        name="ast-filter"
        onChange={(next) => {
          setFilter(next);
          setCursor(0);
        }}
        options={FILTERS.map((value) => ({
          value,
          label: strings.filters[value],
        }))}
        value={filter}
      />
      {current === undefined ? (
        <Notice>
          {filter === "undecided" && summary.undecided === 0
            ? strings.allDone
            : strings.filterEmpty}
        </Notice>
      ) : (
        <ScreeningCard
          duplicateOf={duplicates.get(current)}
          excludeTerms={parseTerms(terms.exclude)}
          includeTerms={parseTerms(terms.include)}
          onClear={() => change(current, null)}
          onDecide={(decision) => decide(decision)}
          onMove={move}
          onNote={(note) => {
            const before = verdicts[current];
            if (before) {
              setVerdict(current, { ...before, note });
            }
          }}
          onReason={(reason) => {
            const before = verdicts[current];
            if (before) {
              setVerdict(current, { ...before, reason });
            }
          }}
          position={position + 1}
          record={records[current]}
          strings={strings}
          total={visible.length}
          verdict={verdicts[current]}
        />
      )}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <p className={HINT}>
        <strong>{strings.shortcutsHeading}:</strong> {strings.shortcuts}
      </p>
      <details className="space-y-3">
        <summary className="cursor-pointer font-medium text-foreground text-sm">
          {strings.termsHeading}
        </summary>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <TextAreaField
            id="ast-include-terms"
            label={strings.includeTermsLabel}
            onChange={(value) => setTerms((c) => ({ ...c, include: value }))}
            placeholder={strings.includeTermsPlaceholder}
            prose
            rows={3}
            value={terms.include}
          />
          <TextAreaField
            id="ast-exclude-terms"
            label={strings.excludeTermsLabel}
            onChange={(value) => setTerms((c) => ({ ...c, exclude: value }))}
            placeholder={strings.excludeTermsPlaceholder}
            prose
            rows={3}
            value={terms.exclude}
          />
        </div>
        <p className={HINT}>{strings.termsHint}</p>
      </details>
      <ScreeningSummaryPanel
        records={records}
        strings={strings}
        summary={summary}
        verdicts={verdicts}
      />
    </section>
  );
}
